import type {
  ActivityEntry,
  BrandSettings,
  BrandUpdate,
  Dataset,
  DatasetLinkSummary,
  DatasetSnapshotMeta,
  OnboardingState,
  ReportDefinition,
  ReportDetail,
  ReportOperation,
  PublicationState,
  PublicReport,
  ReportRevisionRecord,
  SampleKey,
  ResolvedData,
  RevisionEntry,
  SnapshotResult,
  ThemePatch,
  ThemeUpdateResult,
  ThemeView
} from '@tallyframe/core';
import { ApiError, apiErrorFromNetwork, apiErrorFromResponse } from './errors';
import type {
  ApiTokenSummary,
  AppConfig,
  AuthResponse,
  CardPayload,
  ConnectedAgent,
  CreateFromSheetInput,
  CreatedToken,
  CsvPreview,
  ExportPayload,
  GoogleConnection,
  ReportListResponse,
  SheetDescription,
  WorkspaceSummary
} from './types';

/**
 * The one HTTP client.
 *
 * Every mutation carries the double-submit CSRF header and the session cookie
 * (contracts §4). The token lives in memory only — writing it to
 * `localStorage` would hand it to any script that manages to run on the page,
 * and it is cheap to recover from `GET /api/auth/me` after a reload.
 */

const CSRF_HEADER = 'X-Tallyframe-CSRF';

const WORKSPACE_HEADER = 'X-Reportpad-Workspace';

let csrfToken: string | null = null;
let workspaceId: string | null = null;
let reauthenticate: (() => Promise<boolean>) | null = null;
let inFlight: Promise<boolean> | null = null;
const unauthorizedListeners = new Set<() => void>();

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

/** The active workspace, sent on every request except the account routes.
 *  Admin mode never sets one. */
export function setWorkspaceId(id: string | null): void {
  workspaceId = id;
}

/** Runs when a non-anonymous request gets a 401; resolving `true` retries it. */
export function setReauthenticator(fn: (() => Promise<boolean>) | null): void {
  reauthenticate = fn;
}

export function sendsWorkspaceHeader(path: string): boolean {
  return !(
    path.startsWith('/api/public/') ||
    path.startsWith('/internal/') ||
    path.startsWith('/api/auth/') ||
    path.startsWith('/api/account/') ||
    path === '/api/workspaces' ||
    path.startsWith('/api/workspaces/')
  );
}

/** Single-flight: concurrent 401s share one re-exchange. */
async function reauthOnce(): Promise<boolean> {
  if (!reauthenticate) return false;
  inFlight ??= reauthenticate().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

/** Fired on any 401 so the shell can send the person to `/login` from one
 *  place rather than at every call site. */
export function onUnauthorized(listener: () => void): () => void {
  unauthorizedListeners.add(listener);
  return () => {
    unauthorizedListeners.delete(listener);
  };
}

function notifyUnauthorized(): void {
  for (const listener of unauthorizedListeners) listener();
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

interface RequestOptions {
  /** JSON body. Mutually exclusive with `form`. */
  body?: unknown;
  /** Multipart body, for the CSV endpoints. */
  form?: FormData;
  signal?: AbortSignal;
  /** Set for login and the print route, which must not bounce to `/login`. */
  anonymous?: boolean;
  /** Send this workspace instead of the active one (remix picks its target). */
  workspaceOverride?: string;
  /** Internal: this request is already the one retry after a re-exchange. */
  retried?: boolean;
}

async function rawRequest(method: Method, path: string, options: RequestOptions = {}): Promise<Response> {
  const headers: Record<string, string> = {};
  let body: BodyInit | undefined;

  if (options.form) {
    // Let the browser set the multipart boundary itself.
    body = options.form;
  } else if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  if (method !== 'GET' && csrfToken) headers[CSRF_HEADER] = csrfToken;

  if (options.workspaceOverride) headers[WORKSPACE_HEADER] = options.workspaceOverride;
  else if (workspaceId && sendsWorkspaceHeader(path)) headers[WORKSPACE_HEADER] = workspaceId;

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      body,
      credentials: 'include',
      ...(options.signal ? { signal: options.signal } : {})
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw apiErrorFromNetwork(cause);
  }

  if (!response.ok) {
    const error = await apiErrorFromResponse(response);
    if (response.status === 401 && !options.anonymous) {
      if (!options.retried && (await reauthOnce())) {
        return rawRequest(method, path, { ...options, retried: true });
      }
      notifyUnauthorized();
    }
    throw error;
  }
  return response;
}

async function request<T>(method: Method, path: string, options: RequestOptions = {}): Promise<T> {
  const response = await rawRequest(method, path, options);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** A download, fetched rather than navigated to: a navigation to a failing
 *  export would replace the whole app with a JSON error page. */
export interface DownloadResult {
  blob: Blob;
  filename: string;
}

const UTF8_FILENAME = /filename\*=UTF-8''([^;]+)/i;
const ASCII_FILENAME = /filename="([^"]+)"/i;

function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const utf8 = UTF8_FILENAME.exec(header);
  if (utf8 && utf8[1]) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      /* fall through to the ASCII form */
    }
  }
  const ascii = ASCII_FILENAME.exec(header);
  return ascii && ascii[1] ? ascii[1] : fallback;
}

async function download(path: string, fallbackName: string): Promise<DownloadResult> {
  const response = await rawRequest('GET', path);
  return {
    blob: await response.blob(),
    filename: filenameFromDisposition(response.headers.get('content-disposition'), fallbackName)
  };
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export const api = {
  // --- auth ---------------------------------------------------------------
  async login(password: string): Promise<AuthResponse> {
    const result = await request<AuthResponse>('POST', '/api/auth/login', {
      body: { password },
      anonymous: true
    });
    setCsrfToken(result.csrfToken);
    return result;
  },

  async logout(): Promise<void> {
    await request<void>('POST', '/api/auth/logout');
    setCsrfToken(null);
  },

  /** Recovers the session (and the CSRF token) after a page reload. */
  async me(): Promise<AuthResponse> {
    const result = await request<AuthResponse>('GET', '/api/auth/me', { anonymous: true });
    setCsrfToken(result.csrfToken);
    return result;
  },

  async exchange(accessToken: string): Promise<AuthResponse> {
    const result = await request<AuthResponse>('POST', '/api/auth/exchange', {
      body: { accessToken },
      anonymous: true
    });
    setCsrfToken(result.csrfToken);
    return result;
  },

  config(): Promise<AppConfig> {
    return request<AppConfig>('GET', '/api/config', { anonymous: true });
  },

  // --- connected agents (MCP OAuth; person-level, no workspace header) -------
  listConnectedAgents(signal?: AbortSignal): Promise<{ agents: ConnectedAgent[] }> {
    return request<{ agents: ConnectedAgent[] }>('GET', '/api/account/agents', {
      ...(signal ? { signal } : {})
    });
  },

  markConnectionRevoked(id: string): Promise<void> {
    return request<void>('POST', `/api/account/agents/${encodeURIComponent(id)}/revoked`);
  },

  // --- workspaces and tokens ------------------------------------------------
  listWorkspaces(): Promise<{ workspaces: WorkspaceSummary[] }> {
    return request<{ workspaces: WorkspaceSummary[] }>('GET', '/api/workspaces');
  },

  createWorkspace(name: string): Promise<WorkspaceSummary> {
    return request<WorkspaceSummary>('POST', '/api/workspaces', { body: { name } });
  },

  renameWorkspace(id: string, name: string): Promise<WorkspaceSummary> {
    return request<WorkspaceSummary>('PATCH', `/api/workspaces/${encodeURIComponent(id)}`, {
      body: { name }
    });
  },

  switchWorkspace(id: string): Promise<void> {
    return request<void>('POST', `/api/workspaces/${encodeURIComponent(id)}/switch`);
  },

  deleteWorkspace(id: string, confirmName: string): Promise<void> {
    return request<void>('DELETE', `/api/workspaces/${encodeURIComponent(id)}`, { body: { confirmName } });
  },

  deleteAccount(confirmEmail: string): Promise<void> {
    return request<void>('DELETE', '/api/auth/account', { body: { confirmEmail } });
  },

  listTokens(): Promise<{ tokens: ApiTokenSummary[] }> {
    return request<{ tokens: ApiTokenSummary[] }>('GET', '/api/tokens');
  },

  createToken(name: string): Promise<CreatedToken> {
    return request<CreatedToken>('POST', '/api/tokens', { body: { name } });
  },

  revokeToken(id: string): Promise<void> {
    return request<void>('DELETE', `/api/tokens/${encodeURIComponent(id)}`);
  },

  // --- onboarding ---------------------------------------------------------
  getOnboarding(signal?: AbortSignal): Promise<OnboardingState> {
    return request<OnboardingState>('GET', '/api/onboarding', { signal });
  },

  /** 201 when created, 200 when the sample already existed; both resolve to the report. */
  addSample(key: SampleKey): Promise<ReportDetail> {
    return request<ReportDetail>('POST', '/api/samples', { body: { key } });
  },

  // --- reports ------------------------------------------------------------
  listReports(signal?: AbortSignal, workspaceId?: string): Promise<ReportListResponse> {
    return request<ReportListResponse>('GET', '/api/reports', {
      signal,
      ...(workspaceId ? { workspaceOverride: workspaceId } : {})
    });
  },

  getReport(reportId: string, signal?: AbortSignal): Promise<ReportDetail> {
    return request<ReportDetail>('GET', `/api/reports/${encodeURIComponent(reportId)}`, { signal });
  },

  getReportData(reportId: string, snapshotId?: string, signal?: AbortSignal): Promise<ResolvedData> {
    const query = snapshotId ? `?snapshotId=${encodeURIComponent(snapshotId)}` : '';
    return request<ResolvedData>('GET', `/api/reports/${encodeURIComponent(reportId)}/data${query}`, {
      signal
    });
  },

  /** Definition edits. Never touches the data pointer (contracts §3). */
  updateReport(
    reportId: string,
    expectedRevision: number,
    operations: ReportOperation[]
  ): Promise<ReportDetail> {
    return request<ReportDetail>('PATCH', `/api/reports/${encodeURIComponent(reportId)}`, {
      body: { expectedRevision, operations }
    });
  },

  /** Data refresh. Never touches the definition or the revision. */
  refreshReport(
    reportId: string,
    expectedActiveSnapshotId: string | null,
    snapshotId?: string
  ): Promise<ReportDetail> {
    return request<ReportDetail>('POST', `/api/reports/${encodeURIComponent(reportId)}/refresh`, {
      body: snapshotId ? { expectedActiveSnapshotId, snapshotId } : { expectedActiveSnapshotId }
    });
  },

  deleteReport(reportId: string): Promise<void> {
    return request<void>('DELETE', `/api/reports/${encodeURIComponent(reportId)}`);
  },

  createReport(input: {
    datasetId: string;
    title: string;
    snapshotId?: string;
    definition?: ReportDefinition;
    requestId?: string;
  }): Promise<ReportDetail> {
    return request<ReportDetail>('POST', '/api/reports', { body: input });
  },

  /** The straight-through upload path: infer, create and open, server-side. */
  createReportFromCsv(file: File, title: string): Promise<ReportDetail> {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('title', title);
    return request<ReportDetail>('POST', '/api/reports/from-csv', { form });
  },

  // --- google sheets --------------------------------------------------------
  connectGoogle(returnTo: string): Promise<{ url: string }> {
    return request<{ url: string }>('POST', '/api/sources/google/connect', { body: { returnTo } });
  },

  listGoogleConnections(signal?: AbortSignal): Promise<{ connections: GoogleConnection[] }> {
    return request<{ connections: GoogleConnection[] }>('GET', '/api/sources/google', {
      ...(signal ? { signal } : {})
    });
  },

  disconnectGoogle(): Promise<void> {
    return request<void>('DELETE', '/api/sources/google');
  },

  googlePickerToken(): Promise<{ accessToken: string; expiresAt: string }> {
    return request<{ accessToken: string; expiresAt: string }>('POST', '/api/sources/google/picker-token');
  },

  describeSheet(fileId: string): Promise<SheetDescription> {
    return request<SheetDescription>('POST', '/api/sources/google/describe', { body: { fileId } });
  },

  previewSheet(fileId: string, sheetId: number): Promise<CsvPreview> {
    return request<CsvPreview>('POST', '/api/sources/google/preview', { body: { fileId, sheetId } });
  },

  createReportFromSheet(input: CreateFromSheetInput): Promise<ReportDetail> {
    return request<ReportDetail>('POST', '/api/reports/from-google-sheet', { body: input });
  },

  /** A new link or re-pick sends the file and tab; a re-link sends `{}`. */
  saveDatasetLink(datasetId: string, ref: { fileId: string; sheetId: number } | null): Promise<DatasetLinkSummary> {
    return request<DatasetLinkSummary>('PUT', `/api/datasets/${encodeURIComponent(datasetId)}/link`, {
      body: ref ?? {}
    });
  },

  removeDatasetLink(datasetId: string): Promise<void> {
    return request<void>('DELETE', `/api/datasets/${encodeURIComponent(datasetId)}/link`);
  },

  pullDatasetLink(datasetId: string, requestId: string): Promise<SnapshotResult> {
    return request<SnapshotResult>('POST', `/api/datasets/${encodeURIComponent(datasetId)}/link/pull`, {
      body: { requestId }
    });
  },

  // --- history --------------------------------------------------------------
  listReportActivity(reportId: string, before?: number): Promise<{ entries: ActivityEntry[] }> {
    const query = before !== undefined ? `?before=${encodeURIComponent(before)}` : '';
    return request<{ entries: ActivityEntry[] }>(
      'GET',
      `/api/reports/${encodeURIComponent(reportId)}/activity${query}`
    );
  },

  listReportRevisions(reportId: string): Promise<{ revisions: RevisionEntry[] }> {
    return request<{ revisions: RevisionEntry[] }>(
      'GET',
      `/api/reports/${encodeURIComponent(reportId)}/revisions`
    );
  },

  getReportRevision(reportId: string, revision: number): Promise<ReportRevisionRecord> {
    return request<ReportRevisionRecord>(
      'GET',
      `/api/reports/${encodeURIComponent(reportId)}/revisions/${encodeURIComponent(revision)}`
    );
  },

  /** Restores a retained revision's definition under optimistic concurrency,
   *  same as `updateReport`. Never touches the active snapshot. */
  restoreReportRevision(
    reportId: string,
    revision: number,
    expectedRevision: number
  ): Promise<ReportDetail> {
    return request<ReportDetail>(
      'POST',
      `/api/reports/${encodeURIComponent(reportId)}/revisions/${encodeURIComponent(revision)}/restore`,
      { body: { expectedRevision } }
    );
  },

  // --- datasets -----------------------------------------------------------
  previewCsv(file: File): Promise<CsvPreview> {
    const form = new FormData();
    form.append('file', file, file.name);
    return request<CsvPreview>('POST', '/api/datasets/preview-csv', { form });
  },

  createDataset(input: {
    dataset: Omit<Dataset, 'id' | 'createdAt' | 'schemaVersion'>;
    requestId?: string;
  }): Promise<{ dataset: Dataset; deduplicated: boolean }> {
    return request<{ dataset: Dataset; deduplicated: boolean }>('POST', '/api/datasets', {
      body: input
    });
  },

  submitSnapshot(
    datasetId: string,
    input: {
      rows: Array<Record<string, unknown>>;
      sourceDescription?: string;
      sourceObservedAt?: string;
      requestId?: string;
    }
  ): Promise<{ snapshot: DatasetSnapshotMeta; dataset: Dataset }> {
    return request('POST', `/api/datasets/${encodeURIComponent(datasetId)}/snapshots`, { body: input });
  },

  /** Multipart CSV snapshot; the server matches headers to column ids or labels. */
  submitSnapshotCsv(datasetId: string, file: File): Promise<SnapshotResult> {
    const form = new FormData();
    form.append('file', file, file.name);
    return request<SnapshotResult>(
      'POST',
      `/api/datasets/${encodeURIComponent(datasetId)}/snapshots/csv`,
      { form }
    );
  },

  // --- publication ----------------------------------------------------------
  publishReport(reportId: string, expectedRevision?: number): Promise<PublicationState> {
    return request<PublicationState>('POST', `/api/reports/${encodeURIComponent(reportId)}/publication`, {
      body: { expectedRevision }
    });
  },

  republishReport(reportId: string, expectedRevision?: number): Promise<PublicationState> {
    return request<PublicationState>('PUT', `/api/reports/${encodeURIComponent(reportId)}/publication`, {
      body: { expectedRevision }
    });
  },

  /** People only: whether a refresh also moves the link onto the new data. */
  setPublicationAutoUpdate(reportId: string, autoUpdate: boolean): Promise<PublicationState> {
    return request<PublicationState>(
      'PUT',
      `/api/reports/${encodeURIComponent(reportId)}/publication/auto-update`,
      { body: { autoUpdate } }
    );
  },

  unpublishReport(reportId: string): Promise<void> {
    return request<void>('DELETE', `/api/reports/${encodeURIComponent(reportId)}/publication`);
  },

  /** Anonymous: no workspace header, no CSRF. */
  getPublicReport(linkId: string, signal?: AbortSignal): Promise<PublicReport> {
    return request<PublicReport>('GET', `/api/public/${encodeURIComponent(linkId)}`, {
      anonymous: true,
      signal
    });
  },

  remix(
    linkId: string,
    targetWorkspaceId: string
  ): Promise<{ reportId: string; datasetId: string; url: string }> {
    return request('POST', `/api/public/${encodeURIComponent(linkId)}/remix`, {
      body: { workspaceId: targetWorkspaceId },
      workspaceOverride: targetWorkspaceId
    });
  },

  // --- exports ------------------------------------------------------------
  exportCsv(
    reportId: string,
    scope: { kind: 'dataset' } | { kind: 'table'; widgetId: string }
  ): Promise<DownloadResult> {
    const query =
      scope.kind === 'table'
        ? `?scope=table&widgetId=${encodeURIComponent(scope.widgetId)}`
        : '?scope=dataset';
    return download(`/api/reports/${encodeURIComponent(reportId)}/export.csv${query}`, 'report.csv');
  },

  exportPdf(reportId: string): Promise<DownloadResult> {
    return download(`/api/reports/${encodeURIComponent(reportId)}/export.pdf`, 'report.pdf');
  },

  // --- brand ----------------------------------------------------------------
  getBrand(signal?: AbortSignal): Promise<BrandSettings> {
    return request<BrandSettings>('GET', '/api/brand', { signal });
  },

  updateBrand(update: BrandUpdate): Promise<BrandSettings> {
    return request<BrandSettings>('PUT', '/api/brand', { body: update });
  },

  getTheme(signal?: AbortSignal): Promise<ThemeView> {
    return request<ThemeView>('GET', '/api/brand/theme', { signal });
  },

  updateTheme(patch: ThemePatch): Promise<ThemeUpdateResult> {
    return request<ThemeUpdateResult>('PUT', '/api/brand/theme', { body: patch });
  },

  uploadLogo(file: File): Promise<BrandSettings> {
    const form = new FormData();
    form.append('file', file);
    return request<BrandSettings>('PUT', '/api/brand/logo', { form });
  },

  deleteLogo(): Promise<BrandSettings> {
    return request<BrandSettings>('DELETE', '/api/brand/logo');
  },

  // --- print --------------------------------------------------------------
  /** Server-driven, single-use, and explicitly anonymous: the print route runs
   *  in a headless browser that has never logged in (contracts §7). */
  exportPayload(exportId: string, signal?: AbortSignal): Promise<ExportPayload> {
    return request<ExportPayload>('GET', `/internal/export-payload/${encodeURIComponent(exportId)}`, {
      anonymous: true,
      signal
    });
  },

  cardPayload(exportId: string, signal?: AbortSignal): Promise<CardPayload> {
    return request<CardPayload>('GET', `/internal/card-payload/${encodeURIComponent(exportId)}`, {
      anonymous: true,
      signal
    });
  }
};

export { ApiError };
